import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import CloseButton from './CloseButton';
import { lockBodyScroll, unlockBodyScroll } from '../../_utils/useScrollLock';
import './SlidePanel.css';

/**
 * Shared slide panel component.
 * Slides in from the right on desktop, up from the bottom on mobile.
 *
 * Standard mode (with title):
 *   Renders header with title + close, scrollable body, optional footer.
 *
 * Custom layout mode (no title):
 *   Children are rendered directly inside the sheet with no wrapper.
 *   Consumer controls their own header, scrolling, and footer.
 *
 * Props:
 *   title        (string)   - Header title. If omitted, uses custom layout mode.
 *   onClose      (function) - Called after the closing animation finishes
 *   children     (node)     - Body content (scrollable in standard mode)
 *   footer       (node)     - Optional footer content (standard mode only)
 *   headerRight  (node)     - Optional extra elements in the header (standard mode only)
 *   width        (number)   - Desktop panel width in px (default 480)
 *   className    (string)   - Optional extra class on the sheet element
 */
class SlidePanel extends Component {
    constructor(props) {
        super(props);
        this.state = { closing: false };
    }

    componentDidMount() {
        // Shared ref-counted scroll lock: overlapping overlays must not
        // restore each other's locked styles. The old inline version also
        // cleared overflow unconditionally on unmount, which could unlock a
        // different overlay's lock.
        lockBodyScroll();

        // On mobile, scroll focused inputs into view above the keyboard
        if (window.innerWidth < 768) {
            this._focusHandler = (e) => {
                if (e.target.matches('input, textarea, select')) {
                    setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
                }
            };
            document.addEventListener('focusin', this._focusHandler);
        }
    }

    componentWillUnmount() {
        unlockBodyScroll();
        if (this._focusHandler) {
            document.removeEventListener('focusin', this._focusHandler);
        }
    }

    handleClose = () => {
        if (this.state.closing) return;
        this.setState({ closing: true });
        setTimeout(() => {
            if (this.props.onClose) this.props.onClose();
        }, 300);
    }

    render() {
        const { title, children, footer, headerRight, width, className, zIndex, compact } = this.props;
        const { closing } = this.state;
        const closingClass = closing ? ' closing' : '';
        const compactClass = compact ? ' sp-sheet--compact' : '';
        const panelStyle = width ? { width: typeof width === 'number' ? `${width}px` : width } : undefined;
        if (zIndex && panelStyle) { panelStyle.zIndex = zIndex + 1; }
        else if (zIndex) { /* panelStyle is undefined — create it */ }
        const sheetStyle = zIndex ? { ...(panelStyle || {}), zIndex: zIndex + 1 } : panelStyle;
        const overlayStyle = zIndex ? { zIndex } : undefined;
        const sheetClasses = 'sp-sheet' + compactClass + closingClass + (className ? ' ' + className : '');

        const panel = (
            <>
                <div className={'sp-overlay' + closingClass} style={overlayStyle} onClick={this.handleClose} />
                <div className={sheetClasses} style={sheetStyle} onClick={e => e.stopPropagation()}>
                    {title ? (
                        <>
                            <div className="sp-header">
                                <h2 className="sp-header-title">{title}</h2>
                                <div className="sp-header-actions">
                                    {headerRight}
                                    <CloseButton onClick={this.handleClose} />
                                </div>
                            </div>

                            <div className="sp-body">
                                {children}
                            </div>

                            {footer && (
                                <div className="sp-footer">
                                    {footer}
                                </div>
                            )}
                        </>
                    ) : (
                        children
                    )}
                </div>
            </>
        );

        return ReactDOM.createPortal(panel, document.body);
    }
}

export default SlidePanel;
